import { useForm, Head } from '@inertiajs/react'
import Logo from '@/components/Logo'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import type { FormEvent, ReactNode } from 'react'

type Props = {
  organization: {
    name: string
    email: string
    first_name: string | null
    last_name: string | null
    title: string | null
  }
}

export default function CompleteProfile({ organization }: Props) {
  const { data, setData, patch, processing, errors, transform } = useForm({
    name: organization.name,
    first_name: organization.first_name ?? '',
    last_name: organization.last_name ?? '',
    title: organization.title ?? '',
    ein: '',
    cause_description: '',
    website: '',
  })

  function submit(e: FormEvent) {
    e.preventDefault()
    transform(formData => ({ organization: formData }))
    patch('/organizations/complete_profile')
  }

  return (
    <>
      <Head title="Complete Your Profile" />
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background via-accent/10 to-primary/5 px-4">
        <div className="w-full max-w-md">
          <div className="mb-8 flex justify-center">
            <Logo />
          </div>
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="text-3xl font-semibold">Complete your profile</CardTitle>
              <CardDescription>
                Tell us about your nonprofit. Gala Season is for nonprofit organizations only.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">First name</Label>
                    <Input
                      id="first_name"
                      value={data.first_name}
                      onChange={e => setData('first_name', e.target.value)}
                      autoFocus
                    />
                    {errors.first_name && (
                      <p className="text-base text-destructive">{errors.first_name}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name">Last name</Label>
                    <Input
                      id="last_name"
                      value={data.last_name}
                      onChange={e => setData('last_name', e.target.value)}
                    />
                    {errors.last_name && (
                      <p className="text-base text-destructive">{errors.last_name}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="title">Your role</Label>
                  <Input
                    id="title"
                    value={data.title}
                    onChange={e => setData('title', e.target.value)}
                    placeholder="e.g. Executive Director, Development Manager"
                  />
                  {errors.title && (
                    <p className="text-base text-destructive">{errors.title}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="name">Organization name</Label>
                  <Input
                    id="name"
                    value={data.name}
                    onChange={e => setData('name', e.target.value)}
                  />
                  {errors.name && (
                    <p className="text-base text-destructive">{errors.name}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="ein">EIN (Tax ID)</Label>
                  <Input
                    id="ein"
                    value={data.ein}
                    onChange={e => setData('ein', e.target.value)}
                    placeholder="XX-XXXXXXX"
                  />
                  <p className="text-xs text-muted-foreground">
                    Your organization&apos;s federal tax ID. Gala Season is open to registered nonprofits only.
                  </p>
                  {errors.ein && (
                    <p className="text-base text-destructive">{errors.ein}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="website">Website (optional)</Label>
                  <Input
                    id="website"
                    value={data.website}
                    onChange={e => setData('website', e.target.value)}
                    placeholder="https://..."
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cause_description">Describe your cause (optional)</Label>
                  <Textarea
                    id="cause_description"
                    value={data.cause_description}
                    onChange={e => setData('cause_description', e.target.value)}
                    rows={3}
                    placeholder="What does your organization do?"
                  />
                </div>

                <Button type="submit" className="w-full" disabled={processing}>
                  Continue
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  )
}

CompleteProfile.layout = (page: ReactNode) => page
